import "./Helpdesk.css";
import React, { useEffect, useState } from "react";
import { Form, Input, Button, Modal, Card, Row, Col } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, CustomerServiceOutlined } from "@ant-design/icons";
import { createTicket, getTicketsCount } from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import ImageUpload from "../../../components/ImageUpload";
import NewTickets from "../../../components/NewTickets";
import InprogressTickets from "../../../components/InprogressTickets";
import DoneTickets from "../../../components/DoneTickets";
import ClosedTickets from "../../../components/ClosedTickets";
import { handleApiError } from "../../../utils/errorHandler";
import { applyAttachmentToPayload } from "../../../utils/attachmentPayload";

const Helpdesk = () => {
  const ADD_TICKET = "Add Ticket";
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [reload, setReload] = useState(false);
  const [ticketsCount, setTicketsCount] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [modalTitle, setModalTitle] = useState(ADD_TICKET);
  const [image, setImage] = useState(false);
  const [addTicketForm] = Form.useForm();
  const { t } = useTranslation();
  const { TextArea } = Input;

  useEffect(() => {
    getTicketsCount()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTicketsCount(response.data.data);
      })
      .catch((error) => handleApiError(error, t));
  }, [reload, t]);

  const showModal = () => {
    // Set Modal Title
    setModalTitle(t("ticket_add"));
    // Show Modal
    setModalVisible(true);
    // Reset Form Fields
    addTicketForm.resetFields();
    // Reset Image Field
    setImage(false);
  };

  const handleAPIResponse = (response, type) => {
    if (response.data.success === true) {
      setReload(!reload);
      setTickets();
      // Success
      if (type === ADD_TICKET) {
        NotificationWithIcon(
          "success",
          t("ticket_added"),
          t("ticket_added_successfully")
        );
      } else {
        NotificationWithIcon(
          "success",
          t("ticket_updated"),
          t("ticket_updated_successfully")
        );
      }
    } else {
      handleApiError(response, t);
    }
    // Reset Form Fields
    addTicketForm.resetFields();
    // Reset Image Field
    setImage(false);
    // Hide Modal
    setModalVisible(false);
  };

  const handleAddEdit = () => {
    addTicketForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoadingBTN(true);
        // Set Form values as FormData
        const formData = new window.FormData();
        applyAttachmentToPayload(formData, "attachment", image);
        formData.append("description", values.description);

        // Sending Request to API
        if (modalTitle === ADD_TICKET || modalTitle === t("ticket_add")) {
          createTicket(formData)
            .then((response) => handleAPIResponse(response, ADD_TICKET))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleCancel = () => {
    // Reset Form Fields
    addTicketForm.resetFields();
    // Reset Image Field
    setImage(false);
    // Hide Modal
    setModalVisible(false);
  };

  const onChangeTab = (key) => {
    setTickets(key);
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "helpdesk",
      breadcrumbName: t("help_desk"),
      icon: <CustomerServiceOutlined />,
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("help_desk")}
          ghost={false}
          extra={[
            <Button
              key="open-helpdesk-modal"
              type="primary"
              onClick={showModal}
            >
              + {t("ticket_add")}
            </Button>,
          ]}
        />
      </Card>
      <Row
        gutter={[20, 20]}
        style={{ marginBottom: "20px", textAlign: "center" }}
      >
        <Col span={6}>
          <Card
            className={
              "tab_title" + (tickets === "new_tickets" ? " active" : "")
            }
            onClick={() => onChangeTab("new_tickets")}
          >
            <div>
              {t("new")}
              <span className="tab_count">
                {ticketsCount.new_tickets_count}
              </span>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card
            className={
              "tab_title" + (tickets === "inprogress_tickets" ? " active" : "")
            }
            onClick={() => onChangeTab("inprogress_tickets")}
          >
            <div>
              {t("inprogress")}
              <span className="tab_count">
                {ticketsCount.in_progress_tickets_count}
              </span>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card
            className={
              "tab_title" + (tickets === "done_tickets" ? " active" : "")
            }
            onClick={() => onChangeTab("done_tickets")}
          >
            <div>
              {t("done")}
              <span className="tab_count">
                {ticketsCount.done_tickets_count}
              </span>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card
            className={
              "tab_title" + (tickets === "closed_tickets" ? " active" : "")
            }
            onClick={() => onChangeTab("closed_tickets")}
          >
            <div>
              {t("closed")}{" "}
              <span className="tab_count">
                {ticketsCount.closed_tickets_count}
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      {tickets === "new_tickets" && <NewTickets />}
      {tickets === "inprogress_tickets" && <InprogressTickets />}
      {tickets === "done_tickets" && <DoneTickets />}
      {tickets === "closed_tickets" && <ClosedTickets />}

      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddEdit}
        onCancel={handleCancel}
        footer={[
          <Button type="default" key="back" loading={loadingBTN} onClick={handleCancel}>
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={handleAddEdit}
          >
            {modalTitle === ADD_TICKET || modalTitle === t("ticket_add")
              ? t("ticket_add")
              : t("update")}
          </Button>,
        ]}
      >
        <Form
          name="add-helpdesk-form"
          layout="vertical"
          onFinish={handleAddEdit}
          className="app-form-full"
          form={addTicketForm}
        >
          <Form.Item
            name="description"
            label={t("description")}
            rules={[
              {
                required: true,
                message: t("validate_description_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <TextArea rows={4} />
          </Form.Item>

          <div className="ant-col ant-form-item-label">
            <label title={t("attachment")}>{t("attachment")}</label>
          </div>
          <ImageUpload
            name="attachment"
            callbackSetImageFile={setImage}
            image={image}
          />

          <Form.Item name="id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Helpdesk;
